'use client'

import { useEffect, useState } from 'react'
import {
  ActionIcon,
  Button,
  Group,
  Loader,
  Stack,
  Text,
  TextInput,
} from '@mantine/core'
import { IconTrash } from '@tabler/icons-react'
import type { CategoryKeywordItem } from '@/types'
import {
  createCategoryKeyword,
  deleteCategoryKeyword,
  fetchCategoryKeywords,
} from './categoryKeywordsApi'

type LiveProps = {
  variant: 'live'
  categoryId: string
  disabled: boolean
}

type DraftProps = {
  variant: 'draft'
  keywords: string[]
  onChange: (next: string[]) => void
  disabled: boolean
}

type Props = LiveProps | DraftProps

export function CategoryKeywordsSection(props: Props) {
  const disabled = props.disabled
  const [newKeyword, setNewKeyword] = useState('')

  if (props.variant === 'draft') {
    const { keywords, onChange } = props
    function addDraft() {
      const v = newKeyword.trim()
      if (!v) return
      if (keywords.some((k) => k.toLowerCase() === v.toLowerCase())) {
        setNewKeyword('')
        return
      }
      onChange([...keywords, v])
      setNewKeyword('')
    }
    return (
      <Stack gap="sm">
        <Text size="sm" fw={500}>
          Keywords
        </Text>
        <Text size="xs" c="dimmed">
          Descriptions containing these phrases can match this category when
          auto-categorizing.
        </Text>
        <Group gap="xs" wrap="nowrap" align="flex-start">
          <TextInput
            className="min-w-0 flex-1"
            placeholder="e.g. payroll, uber"
            value={newKeyword}
            onChange={(e) => setNewKeyword(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                addDraft()
              }
            }}
            disabled={disabled}
            radius="md"
          />
          <Button
            type="button"
            variant="light"
            onClick={addDraft}
            disabled={disabled || !newKeyword.trim()}
            radius="md"
          >
            Add
          </Button>
        </Group>
        {keywords.length > 0 ? (
          <Stack gap={6}>
            {keywords.map((k) => (
              <Group
                key={k}
                justify="space-between"
                wrap="nowrap"
                gap="xs"
                className="rounded-md border border-slate-200 bg-slate-50/80 px-3 py-2"
              >
                <Text size="sm" lineClamp={1}>
                  {k}
                </Text>
                <ActionIcon
                  type="button"
                  variant="subtle"
                  color="red"
                  size="sm"
                  aria-label={`Remove ${k}`}
                  disabled={disabled}
                  onClick={() => onChange(keywords.filter((x) => x !== k))}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            ))}
          </Stack>
        ) : (
          <Text size="xs" c="dimmed">
            No keywords yet.
          </Text>
        )}
      </Stack>
    )
  }

  const { categoryId } = props
  const [rows, setRows] = useState<CategoryKeywordItem[]>([])
  const [loading, setLoading] = useState(true)
  const [kwError, setKwError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setKwError(null)
    void fetchCategoryKeywords(categoryId)
      .then((list) => {
        if (!cancelled) setRows(list)
      })
      .catch(() => {
        if (!cancelled) setKwError('Could not load keywords')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [categoryId])

  async function addLive() {
    const v = newKeyword.trim()
    if (!v) return
    setKwError(null)
    try {
      const created = await createCategoryKeyword(categoryId, v)
      setRows((prev) => {
        if (prev.some((p) => p.id === created.id)) return prev
        return [...prev, created].sort((a, b) =>
          a.keyword.localeCompare(b.keyword),
        )
      })
      setNewKeyword('')
    } catch (e) {
      setKwError(e instanceof Error ? e.message : 'Could not add keyword')
    }
  }

  async function removeLive(id: string) {
    setKwError(null)
    try {
      await deleteCategoryKeyword(categoryId, id)
      setRows((prev) => prev.filter((r) => r.id !== id))
    } catch (e) {
      setKwError(e instanceof Error ? e.message : 'Could not remove keyword')
    }
  }

  return (
    <Stack gap="sm">
      <Text size="sm" fw={500}>
        Keywords
      </Text>
      <Text size="xs" c="dimmed">
        Descriptions containing these phrases can match this category when
        auto-categorizing. System keywords cannot be removed.
      </Text>
      {loading ? (
        <Loader size="sm" />
      ) : (
        <>
          <Group gap="xs" wrap="nowrap" align="flex-start">
            <TextInput
              className="min-w-0 flex-1"
              placeholder="e.g. payroll, uber"
              value={newKeyword}
              onChange={(e) => setNewKeyword(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  void addLive()
                }
              }}
              disabled={disabled}
              radius="md"
            />
            <Button
              type="button"
              variant="light"
              onClick={() => void addLive()}
              disabled={disabled || !newKeyword.trim()}
              radius="md"
            >
              Add
            </Button>
          </Group>
          {kwError ? (
            <Text size="xs" c="red">
              {kwError}
            </Text>
          ) : null}
          {rows.length > 0 ? (
            <Stack gap={6}>
              {rows.map((row) => {
                const canDelete = row.userId !== null
                return (
                  <Group
                    key={row.id}
                    justify="space-between"
                    wrap="nowrap"
                    gap="xs"
                    className="rounded-md border border-slate-200 bg-slate-50/80 px-3 py-2"
                  >
                    <Text size="sm" lineClamp={1} style={{ flex: 1 }}>
                      {row.keyword}
                    </Text>
                    {canDelete ? (
                      <ActionIcon
                        type="button"
                        variant="subtle"
                        color="red"
                        size="sm"
                        aria-label={`Delete ${row.keyword}`}
                        disabled={disabled}
                        onClick={() => void removeLive(row.id)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    ) : (
                      <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                        default
                      </Text>
                    )}
                  </Group>
                )
              })}
            </Stack>
          ) : (
            <Text size="xs" c="dimmed">
              No keywords yet.
            </Text>
          )}
        </>
      )}
    </Stack>
  )
}
